// ── Reel Match: "how well does this show fit YOU" ────────────────────────────
// Pure math, no supabase/tmdb imports — unit-testable under `node --test`.
// Distinct from tasteMatch.js (person↔person rating agreement) and taste.js
// (archetype labels): this scores a CANDIDATE SHOW against one user's history.
//
// Signals, all client-side from the watchlist the app already holds:
//   rating ≥ 4        → strong positive on the show's genre groups
//   rating ≤ 2.5      → negative
//   dropped (unrated) → negative (you voted with your feet)
//   completed unrated → mild positive (you finished it)
//   rewatch_count > 0 → extra positive (the strongest signal we have)
//   watching now      → mild positive
//
// Honesty rules (the anti-fake-precision contract):
//   - No score at all until the profile has enough fuel (MIN_SIGNALS shows,
//     MIN_RATED of them rated). A thin profile returns { enough:false }.
//   - Percentages are rounded to 5s — "87%" would be a lie about resolution.
//   - Callers should only badge posters at ≥ BADGE_FLOOR; below that the score
//     exists (ShowDetail shows it WITH its reasons) but shouldn't shout.

import { GENRE_GROUPS } from './taste.js'

export const MIN_SIGNALS = 8    // shows contributing any signal
export const MIN_RATED   = 5    // of which this many carry a rating
export const BADGE_FLOOR = 60   // only decorate posters at/above this

// Map a TMDB genre id to its group index (one id → one group).
const GROUP_OF = (() => {
  const m = new Map()
  GENRE_GROUPS.forEach((g, i) => g.ids.forEach(id => m.set(id, i)))
  return m
})()

// TMDB detail objects carry `genres: [{id,name}]`; search/discover results carry
// `genre_ids: [n]`. Cards render both shapes, so read whichever is present.
const genreIdsOf = (show) =>
  (show?.genres || show?.genre_ids || [])
    .map(g => (typeof g === 'object' ? g.id : g))
    .filter(id => GROUP_OF.has(id))

const decadeOf = (show) => {
  const y = parseInt(String(show?.first_air_date || '').slice(0, 4), 10)
  return Number.isFinite(y) ? Math.floor(y / 10) * 10 : null
}

// Per-show taste weight from whatever signals exist. Rating wins when present;
// status only speaks for unrated shows (a dropped-but-rated-4 show is a 4).
function weightOf({ rating, status, rewatch }) {
  let w = 0
  const r = Number(rating)
  if (r > 0) w = r >= 4 ? (r - 3) : r <= 2.5 ? (r - 3) : 0   // 4→+1, 5→+2, 2→−1, 0.5→−2.5
  else if (status === 'dropped') w = -1
  else if (status === 'completed' || status === 'caught_up') w = 0.4
  else if (status === 'watching' || status === 'rewatching') w = 0.25
  if (Number(rewatch) > 0 && w >= 0) w += 0.75
  return w
}

// items: [{ id, genres, first_air_date, rating?, rewatch_count? }]
// ratings/statuses: { showId -> value } (the useWatchlist maps)
export function buildTasteProfile({ items = [], ratings = {}, statuses = {} } = {}) {
  const genreW = new Array(GENRE_GROUPS.length).fill(0)  // summed weight
  const genreN = new Array(GENRE_GROUPS.length).fill(0)  // shows contributing
  const decadeW = {}
  const decadeN = {}
  let signals = 0
  let rated = 0

  for (const show of items) {
    const rating = ratings[show.id] ?? ratings[+show.id] ?? show.rating
    const status = statuses[show.id] ?? statuses[+show.id] ?? show.status
    const w = weightOf({ rating, status, rewatch: show.rewatch_count })
    if (w === 0) continue
    signals++
    if (Number(rating) > 0) rated++

    const groups = [...new Set(genreIdsOf(show).map(id => GROUP_OF.get(id)))]
    // Split the show's weight across its groups so a 3-genre show doesn't
    // count triple; each group still logs one observation for confidence.
    const share = groups.length ? w / groups.length : 0
    for (const gi of groups) { genreW[gi] += share; genreN[gi] += 1 }

    const dec = decadeOf(show)
    if (dec != null) { decadeW[dec] = (decadeW[dec] || 0) + w; decadeN[dec] = (decadeN[dec] || 0) + 1 }
  }

  return {
    enough: signals >= MIN_SIGNALS && rated >= MIN_RATED,
    signals, rated, genreW, genreN, decadeW, decadeN,
  }
}

// Squash an accumulated weight into [-1, 1] with diminishing returns.
const squash = (x) => Math.tanh(x / 3)

// Score one candidate against the profile.
// Returns { enough, pct, reasons } — pct null when enough=false or the show
// carries no usable features (no known genres).
export function scoreShow(profile, show) {
  if (!profile?.enough) return { enough: false, pct: null, reasons: [] }
  const groups = [...new Set(genreIdsOf(show).map(id => GROUP_OF.get(id)))]
  if (!groups.length) return { enough: true, pct: null, reasons: [] }

  // Genre affinity: mean of the candidate's group scores, each squashed.
  // Groups the user has never touched contribute 0 (neutral, not negative).
  const per = groups.map(gi => ({ gi, score: squash(profile.genreW[gi]), n: profile.genreN[gi] }))
  const genreAffinity = per.reduce((s, p) => s + p.score, 0) / per.length

  // Era affinity, softer: how the user feels about this show's decade.
  const dec = decadeOf(show)
  const eraAffinity = dec != null && profile.decadeN[dec] ? squash(profile.decadeW[dec]) : 0

  // Small quality prior so a 5.1-rated genre match doesn't outrank a 8.6 one.
  const va = Number(show.vote_average) || 0
  const quality = va > 0 ? Math.max(0, Math.min(1, (va - 6) / 2.5)) : 0.5

  // Blend to [0,1]: genre carries it; era and quality nudge.
  const raw = 0.5 + genreAffinity * 0.38 + eraAffinity * 0.06 + (quality - 0.5) * 0.12
  // Round to 5s and clamp to a human range — 100% would claim certainty.
  const pct = Math.max(20, Math.min(95, Math.round((raw * 100) / 5) * 5))

  // Reasons: the groups that actually moved the number, strongest first.
  const reasons = per
    .filter(p => p.n >= 2 && Math.abs(p.score) > 0.15)
    .sort((a, b) => Math.abs(b.score) - Math.abs(a.score))
    .slice(0, 3)
    .map(p => ({
      label: GENRE_GROUPS[p.gi].label,
      emoji: GENRE_GROUPS[p.gi].emoji,
      positive: p.score > 0,
      count: p.n,
    }))

  return { enough: true, pct, reasons }
}
